import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Card, Stack, Tooltip, Typography } from '@mui/material'
import { C1Alert, C1Button } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1FormCheckbox } from 'components/core/RHKFormComponents/C1FormCheckbox'
import { C1LabeledTextFieldWithPresets } from 'components/core/RHKFormComponents/C1LabeledTextFieldWithPresets'
import { SearchUsers } from 'components/core/search/users'
import { useTimedState } from 'hooks/useTimedState/useTimedState'
import { Check, Link } from 'lucide-react'
import { User } from 'pbts/c1/api/user/v1/user'
import { FC, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import * as yup from 'yup'

const getSendReminderPresets = (intl: IntlShape): string[] => [
    intl.formatMessage({
        id: 'component_send_reminder_preset_2',
        defaultMessage: 'Your attention is needed on this task.',
    }),
    intl.formatMessage({
        id: 'component_send_reminder_preset_3',
        defaultMessage: 'Kindly review and complete this task.',
    }),
    intl.formatMessage({
        id: 'component_send_reminder_preset_4',
        defaultMessage: 'This is a reminder to review your pending tasks.',
    }),
    intl.formatMessage({
        id: 'component_send_reminder_preset_1',
        defaultMessage: 'Please complete your assigned tasks.',
    }),
]

export type SendReminderFormData = {
    message: string
    notifyManager: boolean
    copiedUsers: User[]
}

type FormFields = SendReminderFormData & {
    ccOthers: boolean
}

export type SendReminderViewProps = {
    open: boolean
    onClose: () => void
    hasReceivers: boolean
    canShowManager: boolean
    canCCOthers: boolean
    onSubmit: (data: SendReminderFormData) => void
    title?: string
    accessReviewLink?: string
}

export const SendReminderView: FC<SendReminderViewProps> = ({
    open,
    onClose,
    hasReceivers,
    canShowManager,
    canCCOthers,
    onSubmit,
    title,
    accessReviewLink,
}) => {
    const intl = useIntl()
    const [isCopied, triggerIsCopied] = useTimedState()

    const handleCopyLink = (): void => {
        if (!accessReviewLink) return
        navigator.clipboard.writeText(accessReviewLink)
        triggerIsCopied()
    }

    const schema = useMemo(() => yup.object({
        message: yup.string()
            .required(intl.formatMessage({
                id: 'component_send_reminder_message_required',
                defaultMessage: 'Message is required',
            }))
            .test('not-empty', intl.formatMessage({
                id: 'component_send_reminder_message_required',
                defaultMessage: 'Message is required',
            }), (value): boolean => !!value?.trim()),
        notifyManager: yup.boolean().required(),
        ccOthers: yup.boolean().required(),
        copiedUsers: yup.array()
            .when('ccOthers', {
                is: true,
                then: (arraySchema) => arraySchema.min(1, intl.formatMessage({
                    id: 'component_send_reminder_copied_users_required',
                    defaultMessage: 'At least one user must be selected',
                })),
            })
            .required(),
    }), [intl])

    const { control, handleSubmit, reset, watch, formState: { errors, isValid } } = useForm<FormFields>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            message: '',
            notifyManager: false,
            ccOthers: false,
            copiedUsers: [],
        },
    })

    const ccOthers = watch('ccOthers')

    const presets = useMemo(() => getSendReminderPresets(intl), [intl])

    useEffect(() => {
        if (!open) {
            reset()
        }
    }, [open, reset])

    const onFormSubmit = ({ ccOthers: _ccOthers, ...formData }: FormFields): void => {
        onSubmit(formData)
    }

    return <C1Dialog open={open} scroll="body" onClose={onClose} fullWidth maxWidth="sm">
        <Card component="form" onSubmit={handleSubmit(onFormSubmit)}>
            <C1DialogTitle>
                {title || <FormattedMessage id="component_send_reminder_title" defaultMessage="Send reminder" />}
            </C1DialogTitle>
            <C1DialogContent>
                <Stack direction="column" spacing={3}>
                    {!hasReceivers && <C1Alert severity="error">
                        <Typography>
                            <FormattedMessage
                                id="component_send_reminder_no_receivers_err"
                                defaultMessage="This task has no assigned users. No reminder can be sent."
                            />
                        </Typography>
                    </C1Alert>}
                    <Stack direction="column" spacing={2}>
                        <Controller
                            name="message"
                            control={control}
                            render={({ field }): React.ReactElement => <C1LabeledTextFieldWithPresets
                                {...field}
                                multiline
                                autoFocus
                                rows={4}
                                presets={presets}
                                setValue={field.onChange}
                                id="message"
                                fullWidth
                                required
                                error={!!errors.message}
                                helperText={errors.message?.message}
                                label={intl.formatMessage({
                                    id: 'component_send_reminder_message_label',
                                    defaultMessage: 'Message',
                                })}
                                placeholder={intl.formatMessage({
                                    id: 'component_send_reminder_message_placeholder',
                                    defaultMessage: 'Please complete your assigned tasks.',
                                })}
                            />}
                        />
                    </Stack>
                    <Stack direction="column" spacing={0}>
                        {canShowManager && <C1FormCheckbox
                            control={control}
                            name="notifyManager"
                            label={intl.formatMessage({
                                id: 'component_send_reminder_notify_manager_label',
                                defaultMessage: "Send a copy to reviewer's manager",
                            })}
                        />}
                        {canCCOthers && <C1FormCheckbox
                            control={control}
                            name="ccOthers"
                            label={intl.formatMessage({
                                id: 'component_send_reminder_cc_label',
                                defaultMessage: 'Send a copy to another user',
                            })}
                        />}
                        {canCCOthers && ccOthers && <Controller
                            name="copiedUsers"
                            control={control}
                            render={({ field }): React.ReactElement => <SearchUsers
                                users={field.value}
                                onSelected={field.onChange}
                                helperText={errors.copiedUsers?.message}
                                disabled={!canCCOthers}
                                error={!!errors.copiedUsers}
                            />}
                        />}
                    </Stack>
                </Stack>
            </C1DialogContent>
            <C1DialogActions sx={{ justifyContent: 'space-between' }}>
                {accessReviewLink ? <Tooltip
                    title={isCopied
                        ? <Stack direction="row" spacing={1} alignItems="center">
                            <C1Icon icon={Check} size="sm" />
                            <FormattedMessage
                                id="component_send_reminder_link_copied"
                                defaultMessage="Link copied"
                            />
                        </Stack>
                        : <FormattedMessage
                            id="component_send_reminder_copy_link_tooltip"
                            defaultMessage="Copy link to clipboard"
                        />
                    }
                >
                    <Box>
                        <C1Button
                            variant="text"
                            startIcon={isCopied ? <C1Icon icon={Check} /> : <C1Icon icon={Link} />}
                            onClick={handleCopyLink}
                            type="button"
                        >
                            <FormattedMessage
                                id="component_send_reminder_copy_link_button"
                                defaultMessage="Copy link to access review"
                            />
                        </C1Button>
                    </Box>
                </Tooltip> : <Box />}
                <Stack direction="row" spacing={1}>
                    <C1Button onClick={onClose} color="info" variant="outlined" type="button">
                        <FormattedMessage id="component_send_reminder_confirmation_cancel" defaultMessage="Cancel" />
                    </C1Button>
                    <C1Button type="submit" color="primary" variant="contained" disabled={!hasReceivers || !isValid}>
                        <FormattedMessage
                            id="component_send_reminder_confirmation_send"
                            defaultMessage="Send reminder now"
                        />
                    </C1Button>
                </Stack>
            </C1DialogActions>
        </Card>
    </C1Dialog>
}
